import * as React from 'react';
import { observer } from 'mobx-react';
import { Mathematics, Coordinate2D, ClubsInfo, Parameter } from 'MeasurementsCalculations';
import { ClubTextValueLabel } from './ClubTextValueLabel/ClubTextValueLabel';
import { IClubControlProps } from './IClubControlProps';
import { ClubTypes } from './ClubTypes';
import { IClubControlState } from './IClubControlState';
import { ReportKind } from 'Data';
import { ClubTypeImage } from './ClubTypeImage';
import { ClubControlAdditionalInfoPopup } from './ClubControlAdditionalInfoPopup/ClubControlAdditionalInfoPopup';

@observer
export class ClubControl extends React.Component<IClubControlProps, IClubControlState> {
  constructor(props: IClubControlProps) {
    super(props, undefined);
    this.state = { ...this.state };
    this.handleSetAdditionalInfoClick = this.handleSetAdditionalInfoClick.bind(this);
  }

  private handleSetAdditionalInfoClick() {}

  private isAverage(): boolean | undefined {
    const reportKind = this.props.reportKind;
    return (
      this.props.selectedAverage &&
      (reportKind === ReportKind.multiGroupReport ||
        reportKind === ReportKind.singleGroupReport ||
        reportKind === ReportKind.puttingMultiGroupReport ||
        reportKind === ReportKind.puttingSingleGroupReport ||
        reportKind === ReportKind.combineTestReport ||
        reportKind === ReportKind.testCenterReport)
    );
  }

  private isNotSingleReport(): boolean {
    const reportKind = this.props.reportKind;
    return (
      reportKind === ReportKind.multiGroupReport ||
      reportKind === ReportKind.singleGroupReport ||
      reportKind === ReportKind.puttingMultiGroupReport ||
      reportKind === ReportKind.puttingSingleGroupReport ||
      reportKind === ReportKind.combineTestReport ||
      reportKind === ReportKind.testCenterReport
    );
  }

  private getClubTypeImage(club: string): ClubTypeImage {
    if (ClubsInfo.Clubs.Iron.indexOf(club) > -1) {
      return ClubTypes.Iron;
    } else if (ClubsInfo.Clubs.Wood.indexOf(club) > -1) {
      return ClubTypes.Wood;
    } else if (ClubsInfo.Clubs.Driver.indexOf(club) > -1) {
      return ClubTypes.Driver;
    } else if (ClubsInfo.Clubs.Wedge.indexOf(club) > -1) {
      return ClubTypes.Wedge;
    } else if (ClubsInfo.Clubs.Hybrid.indexOf(club) > -1) {
      return ClubTypes.Hybrid;
    }

    //fallback solution
    return ClubTypes.Iron;
  }

  private getSpinRateAdditionalInfoPage() {
    return (
      ((this.props.isIos || this.props.isWeb) &&
        `${process.env.REACT_APP_GOLF_PORTAL_URL}/parameters/spinrate`) ||
      ''
    );
  }

  ////#region Clubview helper functions
  private static dynamicLoftGreyArea(
    dynamicLoft: number | undefined,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const dynL = dynamicLoft ?? 0;
    const r1 = height / 6;
    const r2 = height / 3;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r1, dynL);
    let path = 'M ' + centerCoordinate.x + ' ' + (centerCoordinate.y - r1) + ' ';
    path += 'L ' + centerCoordinate.x + ' ' + (centerCoordinate.y - r2) + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r2, 0, dynL, true) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r1, dynL, 0, false);
    return path;
  }

  private static spinLoftGreyArea(
    spinLoft: number,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const spinL = spinLoft == null ? 0 : spinLoft;
    const r1 = width / 6;
    const r2 = width / 4;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r2, spinL + 90);
    let path = 'M ' + (centerCoordinate.x + r1) + ' ' + centerCoordinate.y + ' ';
    path += 'L ' + (centerCoordinate.x + r1) + ' ' + centerCoordinate.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r1, 90, spinL + 90, true) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r2, spinL + 90, 90, false);
    return path;
  }

  private static attackAngleGreyArea(
    attackAngle: number,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const attackA = attackAngle == null ? 0 : attackAngle;
    const r1 = width / 6;
    const r2 = width / 4;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r1, 270 - attackA);
    let path = 'M ' + (centerCoordinate.x - r1) + ' ' + centerCoordinate.y + ' ';
    path += 'L ' + (centerCoordinate.x - r2) + ' ' + centerCoordinate.y + ' ';
    path +=
      this.createArc(centerCoordinate.x, centerCoordinate.y, r2, 270, 270 - attackA, false) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r1, 270 - attackA, 270, true);
    return path;
  }

  //center circle: (x,y)
  private static createArc(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean
  ) {
    const coor = new Coordinate2D(x, y);
    const end = Mathematics.polarToCartesian(coor, radius, endAngle);
    const arcClockwise = clockwise ? 1 : 0;
    const arcSweep = endAngle - startAngle <= 180 ? '0' : '1';
    const d = ['A', radius, radius, 0, arcSweep, arcClockwise, end.x, end.y].join(' ');
    return d;
  }

  private static faceAngleGreyArea(
    faceAngle: number,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const r1 = height / 2 - (height / 2 - height / 3);
    const r2 = r1 - height / 6;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r2, faceAngle + 90);
    let path = 'M ' + (width / 2 + r1) + ' ' + height / 2 + ' ';
    path += 'L ' + (width / 2 + r1) + ' ' + height / 2 + ' ';
    path +=
      this.createArc(centerCoordinate.x, centerCoordinate.y, r1, 90, faceAngle + 90, false) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r2, faceAngle + 90, 90, false);
    return path;
  }

  private static faceToPathGreyArea(
    faceToPath: number,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const r1 = width / 6;
    const r2 = width / 4;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r2, faceToPath + 90);
    let path = 'M ' + (width / 2 + r1) + ' ' + height / 2 + ' ';
    path += 'L ' + (centerCoordinate.x + r1) + ' ' + centerCoordinate.y + ' ';
    path +=
      this.createArc(centerCoordinate.x, centerCoordinate.y, r1, 90, faceToPath + 90, false) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r2, faceToPath + 90, 90, true);
    return path;
  }

  private static clubPathGreyArea(
    clubPath: number,
    width: number,
    height: number,
    centerCoordinate: Coordinate2D
  ) {
    const r1 = width / 6;
    const r2 = width / 4;
    const p4 = Mathematics.polarToCartesian(centerCoordinate, r1, 270 + clubPath);
    let path = 'M ' + (centerCoordinate.x - r1) + ' ' + centerCoordinate.y + ' ';
    path += 'L ' + (centerCoordinate.x - r2) + ' ' + centerCoordinate.y + ' ';
    path +=
      this.createArc(centerCoordinate.x, centerCoordinate.y, r2, 270, 270 + clubPath, true) + ' ';
    path += 'L ' + p4.x + ' ' + p4.y + ' ';
    path += this.createArc(centerCoordinate.x, centerCoordinate.y, r1, 270 + clubPath, 270, false);
    return path;
  }

  private formatUnit(parameter: Parameter, radarValue: number | undefined): string {
    return radarValue ? this.props.unitPresentation.toString(parameter, radarValue) : '-';
  }
  //#endregion

  render() {
    let topClubView = null,
      sideClubView = null;
    const height = 365;
    const width = 510;
    const measurementsData = this.props.measurementsData || this.props.selectedAverage;
    if (measurementsData && this.props.clubType) {
      const dynamicLoft =
          measurementsData.dynamicLoft === undefined ? 0 : measurementsData.dynamicLoft,
        spin = measurementsData.spinRate,
        launchAngle = measurementsData.launchAngle === undefined ? 0 : measurementsData.launchAngle,
        attackAngle = measurementsData.attackAngle === undefined ? 0 : measurementsData.attackAngle,
        faceAngle = measurementsData.faceAngle,
        spinLoft = measurementsData.spinLoft,
        clubPath = measurementsData.clubPath,
        spinAxis = measurementsData.spinAxis,
        launchDirection = measurementsData.launchDirection,
        faceToPath = measurementsData.faceToPath;

      const selectedClubInfo = this.getClubTypeImage(this.props.clubType);

      //calculate scale
      const scaleWidth = 1;
      let scaleHight = 1;
      if (height < 170) {
        scaleHight = 1 - (170 - height) / 170;
      }
      const scale = Math.min(scaleHight, scaleWidth);

      const sideDrawingCenter = new Coordinate2D(width / 2, (2 * height) / 3);
      const topDrawingCenter = new Coordinate2D(width / 2, height / 2);

      //rotationd
      let rotation: string;
      let clubRotation: string | undefined;
      let rotationAngleForClub: number;
      const initialRotationOfTheClub = selectedClubInfo.sideCoef.initialRotation;

      if (dynamicLoft != null) {
        rotation = `rotate(${-dynamicLoft},${sideDrawingCenter.x},${sideDrawingCenter.y})`;
        rotationAngleForClub = dynamicLoft - (initialRotationOfTheClub ?? 0);
        clubRotation = `rotate(${-rotationAngleForClub},${sideDrawingCenter.x},${
          sideDrawingCenter.y
        })`;
      } else {
        rotation = `rotate(${-launchAngle},${sideDrawingCenter.x},${sideDrawingCenter.y})`;
      }
      const attackRotation = `rotate(${-attackAngle},${sideDrawingCenter.x},${
        sideDrawingCenter.y
      })`;
      const launchRotation = `rotate(${-launchAngle},${sideDrawingCenter.x},${
        sideDrawingCenter.y
      })`;
      const rotationTop = `rotate(${faceAngle != null ? faceAngle : 0},${topDrawingCenter.x},${
        topDrawingCenter.y
      })`;
      const rotation90 = `rotate(${90},${topDrawingCenter.x},${topDrawingCenter.y})`;
      const pathRotation = `rotate(${clubPath != null ? clubPath : 0},${topDrawingCenter.x},${
        topDrawingCenter.y
      })`;
      const directionRotation = `rotate(${launchDirection != null ? launchDirection : 0},${
        topDrawingCenter.x
      },${topDrawingCenter.y})`;

      //club image

      const imageScale = 0.6;
      const imageClubHeightSide = imageScale * selectedClubInfo.sideCoef.imageClubWidth;
      const imageClubWidthSide = imageScale * selectedClubInfo.sideCoef.imageClubHeight;
      const xImageSide = sideDrawingCenter.x + selectedClubInfo.sideCoef.xImage * imageScale;
      const yImageSide = sideDrawingCenter.y + selectedClubInfo.sideCoef.yImage * imageScale;

      const imageClubHeightTop = imageScale * selectedClubInfo.topCoef.imageClubWidth;
      const imageClubWidthTop = imageScale * selectedClubInfo.topCoef.imageClubHeight;
      const xImageTop = topDrawingCenter.x + selectedClubInfo.topCoef.xImage * imageScale;
      const yImageTop = topDrawingCenter.y + selectedClubInfo.topCoef.yImage * imageScale;
      //ball
      const imageScaleBall = 0.3;
      const imageBallHeight = imageScaleBall * 200;
      const imageBallWidth = imageScaleBall * 200;
      const xBall = (3 * width) / 4 + 20;
      const imagePathBall = 'Images/ClubControl/Ball.png';
      const yBallSide = sideDrawingCenter.y - imageBallHeight / 2;
      const yBallTop = topDrawingCenter.y - imageBallHeight / 2;

      topClubView = (
        <g id="left-side-scale" transform={`scale(${scale})`}>
          <path
            d={ClubControl.dynamicLoftGreyArea(dynamicLoft, width, height, sideDrawingCenter)}
            stroke="none"
            strokeWidth="2"
            fill="lightgrey"
            transform={rotation}
          ></path>
          {dynamicLoft && attackAngle && (
            <path
              id="left-side-spinLoftGreyArea"
              d={ClubControl.spinLoftGreyArea(dynamicLoft, width, height, sideDrawingCenter)}
              stroke="none"
              strokeWidth="2"
              fill="lightgrey"
              transform={rotation}
            ></path>
          )}
          {attackAngle && (
            <path
              d={ClubControl.attackAngleGreyArea(attackAngle, width, height, sideDrawingCenter)}
              stroke="none"
              strokeWidth="2"
              fill="lightgrey"
            ></path>
          )}
          <line
            x1={width / 4}
            y1={sideDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={sideDrawingCenter.y}
            stroke="blue"
            strokeWidth="2"
            fill="none"
            transform={attackRotation}
          ></line>
          <line
            x1={width / 4}
            y1={sideDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={sideDrawingCenter.y}
            stroke="grey"
            strokeWidth="2"
            fill="none"
          ></line>
          <line
            x1={sideDrawingCenter.x}
            y1={sideDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={sideDrawingCenter.y}
            stroke="red"
            strokeWidth="2"
            fill="none"
            transform={rotation}
          ></line>
          <line
            x1={sideDrawingCenter.x}
            y1={sideDrawingCenter.y}
            x2={(3 * width) / 4 + 20}
            y2={sideDrawingCenter.y}
            stroke="grey"
            strokeWidth="2"
            strokeDasharray="5"
            fill="none"
            transform={launchRotation}
          ></line>
          <image
            xlinkHref={selectedClubInfo.sideImgPath}
            height={imageClubHeightSide}
            width={imageClubWidthSide}
            x={xImageSide}
            y={yImageSide}
            transform={clubRotation}
          ></image>
          <image
            xlinkHref={imagePathBall}
            height={imageBallHeight}
            width={imageBallWidth}
            x={xBall}
            y={yBallSide}
            transform={launchRotation}
          ></image>
          <line
            x1={sideDrawingCenter.x}
            y1={sideDrawingCenter.y}
            x2={width / 2}
            y2={sideDrawingCenter.y - height / 3}
            stroke="red"
            strokeWidth="2"
            fill="none"
            transform={rotation}
          ></line>
          <ClubTextValueLabel
            x={width}
            y={(11 * height) / 12}
            text="Spin Loft"
            value={this.formatUnit(Parameter.SpinLoft, spinLoft)}
            textAnchor="end"
          />
          <ClubTextValueLabel
            x={0}
            y={(11 * height) / 12}
            text="Attack Angle"
            value={this.formatUnit(Parameter.AttackAngle, attackAngle)}
          />
          <ClubTextValueLabel
            x={0}
            y={20}
            text="Dynamic Loft"
            value={this.formatUnit(Parameter.DynamicLoft, dynamicLoft)}
          />
          <ClubTextValueLabel
            x={width}
            y={20}
            text="Spin Rate"
            value={this.formatUnit(Parameter.SpinRate, spin)}
            textAnchor="end"
            setSpinRateAdditionalInfo={this.handleSetAdditionalInfoClick}
            additionalInformationId="#showAdditionalInfoOnClub"
            valueType="rpm"
          />
          <circle cx={sideDrawingCenter.x} cy={sideDrawingCenter.y} r="1" fill="green"></circle>
        </g>
      );

      sideClubView = (
        <g id="left-top-scale" transform={`scale(${scale})`}>
          {faceAngle && (
            <path
              d={ClubControl.faceAngleGreyArea(faceAngle, width, height, topDrawingCenter)}
              stroke="none"
              strokeWidth="2"
              fill="lightgrey"
              transform={rotation90}
            ></path>
          )}
          {faceToPath && (
            <path
              d={ClubControl.faceToPathGreyArea(faceToPath, width, height, topDrawingCenter)}
              stroke="none"
              strokeWidth="2"
              fill="lightgrey"
              transform={pathRotation}
            ></path>
          )}
          {clubPath && (
            <path
              id="left-top-clubPathGreyArea"
              d={ClubControl.clubPathGreyArea(clubPath, width, height, topDrawingCenter)}
              stroke="none"
              strokeWidth="2"
              fill="lightgrey"
            ></path>
          )}
          <line
            x1={width / 4}
            y1={topDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={topDrawingCenter.y}
            stroke="blue"
            strokeWidth="2"
            fill="none"
            transform={pathRotation}
          ></line>
          <line
            x1={width / 4}
            y1={topDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={topDrawingCenter.y}
            stroke="grey"
            strokeWidth="2"
            fill="none"
          ></line>
          <line
            x1={topDrawingCenter.x}
            y1={topDrawingCenter.y}
            x2={(3 * width) / 4}
            y2={topDrawingCenter.y}
            stroke="red"
            strokeWidth="2"
            fill="none"
            transform={rotationTop}
          ></line>
          <line
            x1={topDrawingCenter.x}
            y1={topDrawingCenter.y}
            x2={(3 * width) / 4 + 20}
            y2={topDrawingCenter.y}
            stroke="grey"
            strokeWidth="2"
            strokeDasharray="5"
            fill="none"
            transform={directionRotation}
          ></line>
          <image
            xlinkHref={selectedClubInfo.topImgPath}
            height={imageClubHeightTop}
            width={imageClubWidthTop}
            x={xImageTop}
            y={yImageTop}
            transform={rotationTop}
          ></image>
          <image
            xlinkHref={imagePathBall}
            height={imageBallHeight}
            width={imageBallWidth}
            x={xBall}
            y={yBallTop}
            transform={directionRotation}
          ></image>
          <line
            x1={topDrawingCenter.x}
            y1={topDrawingCenter.y}
            x2={topDrawingCenter.x}
            y2={topDrawingCenter.y + height / 3}
            stroke="red"
            strokeWidth="2"
            fill="none"
            transform={rotationTop}
          ></line>
          <ClubTextValueLabel
            x={width}
            y={20}
            text="Face To Path"
            value={this.formatUnit(Parameter.FaceToPath, faceToPath)}
            textAnchor="end"
          />
          <ClubTextValueLabel
            x={0}
            y={20}
            text="Club Path"
            value={this.formatUnit(Parameter.ClubPath, clubPath)}
          />
          <ClubTextValueLabel
            x={0}
            y={(11 * height) / 12}
            text="Face Angle"
            value={this.formatUnit(Parameter.FaceAngle, faceAngle)}
          />
          <ClubTextValueLabel
            x={width}
            y={(11 * height) / 12}
            text="Spin Axis"
            value={this.formatUnit(Parameter.SpinAxis, spinAxis)}
            textAnchor="end"
          />
          <circle cx={topDrawingCenter.x} cy={topDrawingCenter.y} r="1" fill="green"></circle>
        </g>
      );
    }

    return (
      <div className="club-control">
        {this.isAverage() && (
          <div className="average-text">
            <p>Average</p>
          </div>
        )}
        <div id="shotviewer-left-clubview-container" className="shotviewer-clubview-container">
          {this.isNotSingleReport() && this.props.selectedStrokesGroups.length === 0 && (
            <div className="info-container">
              <p>You have no groups selected.</p>
              <p>Select a group and a shot to activate the optimizer.</p>
            </div>
          )}
          {this.isNotSingleReport() &&
            this.props.selectedStrokesGroups.length !== 0 &&
            !(this.props.selectedStroke || this.props.selectedAverage) && (
              <div className="info-container">
                <p>You have 0 strokes selected.</p>
                <p>Select 1 stroke or average below to get started.</p>
              </div>
            )}
          {this.isNotSingleReport() &&
            (this.props.selectedStroke || this.props.selectedAverage) &&
            !this.props.clubType && (
              <div className="info-container">
                <p>The club type is not specified for this stroke.</p>
                <p>Club view not available.</p>
              </div>
            )}
          {(this.props.reportKind === ReportKind.singleShotReport ||
            this.props.reportKind === ReportKind.puttingSingleShotReport) &&
            (this.props.clubType === '' || this.props.clubType == null) && (
              <div className="info-container">
                <p>The club type is not specified.</p>
                <p>Club view not available.</p>
              </div>
            )}
          <div className="shotviewer-top-clubview col-xs-6 shotviewerTopClubviewInMultigroup">
            <div
              id="left-top-clubview-canvas"
              className="clubview-canvas col-xs-12 leftTopClubviewCanvasInMultigroup"
            >
              <svg id="left-canvas-clubview-top-svg" width={width} height={height}>
                {topClubView}
              </svg>
            </div>
          </div>
          <div className="shotviewer-side-clubview col-xs-6 shotviewerSideClubviewInMultigroup">
            <div
              id="left-top-clubview-canvas"
              className="clubview-canvas col-xs-12 leftTopClubviewCanvasInMultigroup"
            >
              <svg id="left-canvas-clubview-top-svg" width={width} height={height}>
                {sideClubView}
              </svg>
            </div>
          </div>
        </div>
        <ClubControlAdditionalInfoPopup url={this.getSpinRateAdditionalInfoPage()} />
      </div>
    );
  }
}
